import React, { useState, useEffect, useMemo } from 'react';
import { DollarSign, Settings2, Check, AlertTriangle, Loader2, TrendingUp, Wallet, Save, X, ChevronDown, ChevronRight, ExternalLink, Users, Filter } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { PaymentProgress } from './PaymentProgress';
import { formatEUR, getProjectValueEUR, fetchExchangeRates } from '../utils/currencyUtils';
import ProjectModal from './ProjectModal';
import Avatar from './Avatar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getStatusName = (project) => project?.field_85_raw?.[0]?.identifier || 'No Status';

const daysOverdue = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return Math.floor((now - d) / (1000 * 60 * 60 * 24));
};

const overdueColor = (days) => {
  if (days > 30) return 'text-red-600';
  if (days > 0) return 'text-orange-600';
  return 'text-gray-900';
};

const FinanceAdmin = () => {
  const { token, hasRole } = useAuth();
  const isAdmin = hasRole('admin');

  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [activeStatuses, setActiveStatuses] = useState(null); // null => all active
  const [showConfig, setShowConfig] = useState(false);
  const [configDraft, setConfigDraft] = useState([]);
  const [savingConfig, setSavingConfig] = useState(false);
  const [expandedStatus, setExpandedStatus] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [userAvatars, setUserAvatars] = useState({});
  const [rates, setRates] = useState(null);
  const [visibleUserIds, setVisibleUserIds] = useState([]);
  const [filteredOwners, setFilteredOwners] = useState(() => {
    const saved = localStorage.getItem('financeFilteredOwners');
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    localStorage.setItem('financeFilteredOwners', JSON.stringify(filteredOwners));
  }, [filteredOwners]);

  useEffect(() => {
    if (token) loadData();
  }, [token]);

  const loadData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [projRes, statusRes, configRes] = await Promise.all([
        axios.get(`${API}/projects`, { headers, params: { rows_per_page: 1000 } }),
        axios.get(`${API}/reference-data/project-status`, { headers }),
        axios.get(`${API}/settings/finance-config`, { headers }).catch(() => ({ data: { active_statuses: null } })),
      ]);
      const records = projRes.data.records || [];
      setProjects(records);
      const sorted = (statusRes.data?.records || statusRes.data || []).sort(
        (a, b) => (a.order ?? 999) - (b.order ?? 999)
      );
      setStatusOptions(sorted);
      setActiveStatuses(configRes.data?.active_statuses ?? null);

      // Fetch owner avatars
      const ownerIds = [...new Set(records.map((p) => p.field_237_raw?.[0]?.id).filter(Boolean))];
      if (ownerIds.length > 0) {
        try {
          const a = await axios.post(`${API}/users/avatars`, { user_ids: ownerIds }, { headers });
          setUserAvatars(a.data?.avatars || {});
        } catch (e) {
          console.error('Failed to fetch avatars:', e);
        }
      }

      // Exchange rates + finance-section user visibility
      const r = await fetchExchangeRates(API, token);
      setRates(r);
      try {
        const vis = await axios.get(`${API}/settings/user-visibility`, { headers });
        setVisibleUserIds(vis.data?.finance || []);
      } catch (e) {
        setVisibleUserIds([]);
      }
    } catch (error) {
      console.error('Failed to load finance data:', error);
    } finally {
      setLoading(false);
    }
  };

  const allStatusNames = useMemo(() => statusOptions.map((s) => s.field_84_raw), [statusOptions]);

  const isStatusActive = (name) => activeStatuses === null || activeStatuses.includes(name);

  // Unique project owners (deduped by id) for the filter bar, limited to the Finance section's visible users
  const projectOwners = useMemo(() => {
    const seen = [];
    projects.forEach((p) => {
      const o = p.field_237_raw?.[0];
      if (o?.id && !seen.find((u) => u.id === o.id) && (visibleUserIds.length === 0 || visibleUserIds.includes(o.id))) seen.push(o);
    });
    return seen.sort((a, b) => (a.identifier || '').localeCompare(b.identifier || ''));
  }, [projects, visibleUserIds]);

  // Apply owner filter across the whole dashboard
  const filteredProjects = useMemo(() => {
    if (filteredOwners.length === 0) return projects;
    return projects.filter((p) => filteredOwners.includes(p.field_237_raw?.[0]?.id));
  }, [projects, filteredOwners]);

  const toggleOwnerFilter = (ownerId) => {
    setFilteredOwners((prev) => (prev.includes(ownerId) ? prev.filter((id) => id !== ownerId) : [...prev, ownerId]));
  };

  // Payment aggregate over an arbitrary list of projects
  const computeStatusPayment = (list) => {
    let expected = 0;
    let paid = 0;
    (list || []).forEach((p) => {
      const m = p.payment_milestones || [];
      if (m.length === 0) return;
      const value = getProjectValueEUR(p, rates);
      const paidPct = m.filter((x) => x.paid).reduce((s, x) => s + (parseFloat(x.percentage) || 0), 0);
      expected += value;
      paid += value * (paidPct / 100);
    });
    return { expected, paid, outstanding: expected - paid, pct: expected > 0 ? Math.round((paid / expected) * 100) : 0 };
  };

  // Value + count per status
  const valueByStatus = useMemo(() => {
    const map = {};
    allStatusNames.forEach((name) => { map[name] = { total: 0, count: 0 }; });
    filteredProjects.forEach((p) => {
      const name = getStatusName(p);
      if (!map[name]) map[name] = { total: 0, count: 0 };
      map[name].total += getProjectValueEUR(p, rates);
      map[name].count += 1;
    });
    return map;
  }, [filteredProjects, allStatusNames, rates]);

  // Projects grouped by status name (for expandable cards)
  const projectsByStatus = useMemo(() => {
    const map = {};
    filteredProjects.forEach((p) => {
      const name = getStatusName(p);
      if (!map[name]) map[name] = [];
      map[name].push(p);
    });
    // Sort each group by value desc
    Object.values(map).forEach((list) => list.sort((a, b) => getProjectValueEUR(b, rates) - getProjectValueEUR(a, rates)));
    return map;
  }, [filteredProjects, rates]);

  // Payment aggregates
  const paymentAgg = useMemo(() => {
    let expected = 0;
    let paid = 0;
    const perProject = [];
    filteredProjects.forEach((p) => {
      const milestones = p.payment_milestones || [];
      if (milestones.length === 0) return;
      const value = getProjectValueEUR(p, rates);
      const paidPct = milestones.filter((m) => m.paid).reduce((s, m) => s + (parseFloat(m.percentage) || 0), 0);
      const paidAmount = value * (paidPct / 100);
      expected += value;
      paid += paidAmount;
      perProject.push({
        id: p.id,
        name: p.field_5_raw || 'Untitled',
        ge: p.field_2_raw || '',
        value,
        paidPct: Math.min(100, Math.round(paidPct)),
        paidAmount,
        milestones,
      });
    });
    perProject.sort((a, b) => b.value - a.value);
    return { expected, paid, perProject, overallPct: expected > 0 ? Math.round((paid / expected) * 100) : 0 };
  }, [filteredProjects, rates]);

  // Overdue milestones
  const overdueRows = useMemo(() => {
    const rows = [];
    filteredProjects.forEach((p) => {
      const value = getProjectValueEUR(p, rates);
      (p.payment_milestones || []).forEach((m) => {
        if (m.paid) return;
        const d = daysOverdue(m.estimated_date);
        if (d !== null && d > 0) {
          rows.push({
            projectId: p.id,
            projectName: p.field_5_raw || 'Untitled',
            ge: p.field_2_raw || '',
            percentage: parseFloat(m.percentage) || 0,
            estimated_date: m.estimated_date,
            days: d,
            amount: value * ((parseFloat(m.percentage) || 0) / 100),
            notes: m.notes,
          });
        }
      });
    });
    rows.sort((a, b) => b.days - a.days);
    return rows;
  }, [filteredProjects, rates]);

  const grandTotal = useMemo(
    () => Object.values(valueByStatus).reduce((s, v) => s + v.total, 0),
    [valueByStatus]
  );

  const openConfig = () => {
    setConfigDraft(activeStatuses === null ? [...allStatusNames] : [...activeStatuses]);
    setShowConfig(true);
  };

  const toggleDraft = (name) => {
    setConfigDraft((prev) => (prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]));
  };

  const saveConfig = async () => {
    setSavingConfig(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const value = configDraft.length === allStatusNames.length ? null : configDraft;
      await axios.put(`${API}/settings/finance-config`, { active_statuses: value }, { headers });
      setActiveStatuses(value);
      setShowConfig(false);
    } catch (error) {
      console.error('Failed to save config:', error);
      alert('Failed to save configuration: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSavingConfig(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-8 h-8 animate-spin text-green-600" />
      </div>
    );
  }

  const activeCards = allStatusNames.filter((name) => isStatusActive(name) && valueByStatus[name]);

  return (
    <div className="p-6 space-y-8" data-testid="finance-admin-dashboard">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-1 flex items-center gap-2">
            <DollarSign className="w-7 h-7 text-green-600 dark:text-green-400" />
            Finance Admin
          </h1>
          <p className="text-gray-600 dark:text-gray-400">Total project value by status, payment progress and overdue payments</p>
        </div>
        {isAdmin && (
          <button
            onClick={openConfig}
            className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition text-sm font-medium text-gray-700 dark:text-gray-200"
            data-testid="finance-configure-btn"
          >
            <Settings2 className="w-4 h-4" />
            Configure Indicators
          </button>
        )}
      </div>

      {/* Grand total */}
      <div className="bg-gradient-to-r from-green-600 to-emerald-600 rounded-xl p-6 text-white shadow" data-testid="finance-grand-total">
        <p className="text-sm uppercase tracking-wide text-green-100">Total Portfolio Value (visible statuses)</p>
        <p className="text-4xl font-bold mt-1">
          {formatEUR(activeCards.reduce((s, n) => s + valueByStatus[n].total, 0))}
        </p>
        <p className="text-sm text-green-100 mt-1">
          Across {activeCards.reduce((s, n) => s + valueByStatus[n].count, 0)} projects · Full portfolio: {formatEUR(grandTotal)}
        </p>
      </div>

      {/* Owner filter bar */}
      {projectOwners.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4" data-testid="finance-owner-filter">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-200">
              <Filter className="w-4 h-4" />
              Filter by owner
              {filteredOwners.length > 0 && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">{filteredOwners.length} selected</span>
              )}
            </div>
            {filteredOwners.length > 0 && (
              <button
                onClick={() => setFilteredOwners([])}
                className="text-xs text-gray-500 hover:text-gray-700 underline"
                data-testid="finance-owner-filter-clear"
              >
                Clear
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {projectOwners.map((owner) => {
              const selected = filteredOwners.includes(owner.id);
              return (
                <button
                  key={owner.id}
                  type="button"
                  onClick={() => toggleOwnerFilter(owner.id)}
                  title={owner.identifier}
                  className={`flex items-center gap-2 pl-1 pr-3 py-1 rounded-full border transition ${selected ? 'border-green-500 bg-green-50 dark:bg-green-900/20 ring-1 ring-green-500' : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                  data-testid={`finance-owner-chip-${owner.id}`}
                >
                  <Avatar userId={owner.id} userName={owner.identifier} avatarUrl={userAvatars[owner.id] || null} size="sm" />
                  <span className="text-xs text-gray-700 dark:text-gray-200">{owner.identifier}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Status value indicators */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Value by Status</h2>
        {activeCards.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {activeCards.map((name) => {
              const { total, count } = valueByStatus[name];
              const pct = grandTotal > 0 ? Math.round((total / grandTotal) * 100) : 0;
              const isExpanded = expandedStatus === name;
              return (
                <button
                  key={name}
                  type="button"
                  onClick={() => setExpandedStatus(isExpanded ? null : name)}
                  className={`text-left bg-white dark:bg-gray-800 rounded-lg shadow p-4 border transition hover:shadow-md ${isExpanded ? 'border-green-500 ring-1 ring-green-500' : 'border-gray-100 dark:border-gray-700'}`}
                  data-testid={`finance-status-card-${name}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-300 truncate flex items-center gap-1">
                      {isExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                      {name}
                    </span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">{count}</span>
                  </div>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{formatEUR(total)}</p>
                  <div className="mt-2 w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div className="h-full bg-green-500" style={{ width: `${pct}%` }} />
                  </div>
                  <p className="text-xs text-gray-400 mt-1">{pct}% of portfolio · click to view projects</p>
                </button>
              );
            })}
          </div>
        ) : (
          <p className="text-gray-500 text-sm">No status indicators enabled. Use "Configure Indicators" to enable some.</p>
        )}

        {/* Expanded status project list */}
        {expandedStatus && (() => {
          const statusProjects = projectsByStatus[expandedStatus] || [];
          const sp = computeStatusPayment(statusProjects);
          return (
          <div className="mt-4 bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden" data-testid="finance-status-expanded">
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h3 className="font-semibold text-gray-900 dark:text-white text-sm">
                {expandedStatus} — {statusProjects.length} projects
              </h3>
              <button onClick={() => setExpandedStatus(null)} className="text-gray-400 hover:text-gray-600" data-testid="finance-status-expanded-close">
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Per-status Payment Progress Overview */}
            <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900/40 border-b border-gray-200 dark:border-gray-700" data-testid="finance-status-payment-overview">
              <div className="flex items-center gap-2 mb-2 text-xs font-medium text-gray-600 dark:text-gray-300">
                <Wallet className="w-3.5 h-3.5 text-blue-600" />
                Payment Progress Overview
              </div>
              <div className="grid grid-cols-3 gap-3 mb-2">
                <div>
                  <p className="text-[11px] text-gray-500">Expected</p>
                  <p className="text-sm font-bold text-gray-900 dark:text-white">{formatEUR(sp.expected)}</p>
                </div>
                <div>
                  <p className="text-[11px] text-gray-500">Collected</p>
                  <p className="text-sm font-bold text-green-600">{formatEUR(sp.paid)}</p>
                </div>
                <div>
                  <p className="text-[11px] text-gray-500">Outstanding</p>
                  <p className="text-sm font-bold text-orange-600">{formatEUR(sp.outstanding)}</p>
                </div>
              </div>
              <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div className="h-full bg-green-500 transition-all" style={{ width: `${sp.pct}%` }} />
              </div>
              <p className="text-[11px] text-gray-500 mt-1">{sp.pct}% collected</p>
            </div>

            <div className="divide-y divide-gray-100 dark:divide-gray-700 max-h-96 overflow-y-auto">
              {statusProjects.map((p) => {
                const owner = p.field_237_raw?.[0];
                return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setSelectedProject(p)}
                  className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition group"
                  data-testid={`finance-status-project-${p.id}`}
                >
                  {owner ? (
                    <div title={owner.identifier} className="flex-shrink-0" data-testid={`finance-project-owner-${p.id}`}>
                      <Avatar userId={owner.id} userName={owner.identifier} avatarUrl={userAvatars[owner.id] || null} size="sm" />
                    </div>
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 flex-shrink-0" title="No owner" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate group-hover:text-green-600">{p.field_5_raw || 'Untitled'}</p>
                    <p className="text-xs text-gray-500">{p.field_2_raw || 'No ID'} · {formatEUR(getProjectValueEUR(p, rates))}</p>
                  </div>
                  <div className="w-32 flex-shrink-0">
                    {p.payment_milestones?.length > 0 ? (
                      <PaymentProgress milestones={p.payment_milestones} variant="bar" />
                    ) : (
                      <span className="text-xs text-gray-400">No milestones</span>
                    )}
                  </div>
                  <ExternalLink className="w-4 h-4 text-gray-300 group-hover:text-green-600 flex-shrink-0" />
                </button>
                );
              })}
            </div>
          </div>
          );
        })()}
      </div>

      {/* Payment progress overview */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <Wallet className="w-5 h-5 text-blue-600" />
          Payment Progress Overview
        </h2>

        {/* Aggregate */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-4" data-testid="finance-payment-aggregate">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <p className="text-sm text-gray-500">Expected (projects with milestones)</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{formatEUR(paymentAgg.expected)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Collected</p>
              <p className="text-2xl font-bold text-green-600">{formatEUR(paymentAgg.paid)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Outstanding</p>
              <p className="text-2xl font-bold text-orange-600">{formatEUR(paymentAgg.expected - paymentAgg.paid)}</p>
            </div>
          </div>
          <div className="w-full h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div className="h-full bg-green-500 transition-all" style={{ width: `${paymentAgg.overallPct}%` }} />
          </div>
          <p className="text-sm text-gray-500 mt-1">{paymentAgg.overallPct}% collected overall</p>
        </div>

        {/* Per-project list */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden" data-testid="finance-payment-perproject">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <h3 className="font-semibold text-gray-900 dark:text-white text-sm">Projects with Payment Milestones ({paymentAgg.perProject.length})</h3>
          </div>
          {paymentAgg.perProject.length > 0 ? (
            <div className="divide-y divide-gray-100 dark:divide-gray-700 max-h-96 overflow-y-auto">
              {paymentAgg.perProject.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setSelectedProject(projects.find((pr) => pr.id === p.id))}
                  className="w-full text-left px-4 py-3 flex items-center gap-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition group"
                  data-testid={`finance-perproject-${p.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate group-hover:text-green-600 flex items-center gap-1">
                      {p.name}
                      <ExternalLink className="w-3.5 h-3.5 text-gray-300 group-hover:text-green-600 flex-shrink-0" />
                    </p>
                    <p className="text-xs text-gray-500">{p.ge} · {formatEUR(p.paidAmount)} / {formatEUR(p.value)}</p>
                  </div>
                  <div className="w-40 flex-shrink-0">
                    <PaymentProgress milestones={p.milestones} variant="bar" />
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-sm p-4">No projects have payment milestones yet.</p>
          )}
        </div>
      </div>

      {/* Overdue milestones */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-red-600" />
          Overdue Payments ({overdueRows.length})
        </h2>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden" data-testid="finance-overdue-table">
          {overdueRows.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Project</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Milestone %</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Est. Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Days Overdue</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {overdueRows.map((r, i) => (
                    <tr key={`${r.projectId}-${i}`} onClick={() => setSelectedProject(projects.find((pr) => pr.id === r.projectId))} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer group" data-testid={`finance-overdue-row-${i}`}>
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate max-w-xs group-hover:text-green-600 flex items-center gap-1">
                          {r.projectName}
                          <ExternalLink className="w-3.5 h-3.5 text-gray-300 group-hover:text-green-600 flex-shrink-0" />
                        </p>
                        {r.notes && <p className="text-xs text-gray-400 truncate max-w-xs">{r.notes}</p>}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{r.percentage}%</td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">{r.estimated_date}</td>
                      <td className={`px-4 py-3 text-sm font-semibold ${overdueColor(r.days)}`}>{r.days}d</td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">{formatEUR(r.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-500 text-sm p-4">No overdue payments. 🎉</p>
          )}
        </div>
      </div>

      {/* Configure modal */}
      {showConfig && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6 max-h-[85vh] overflow-y-auto" data-testid="finance-config-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Configure Status Indicators</h3>
              <button onClick={() => setShowConfig(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-gray-500 mb-4">Choose which status indicators appear on the Finance dashboard. Saved for everyone.</p>
            <div className="space-y-1 mb-6">
              {allStatusNames.map((name) => (
                <label key={name} className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer" data-testid={`finance-config-option-${name}`}>
                  <input
                    type="checkbox"
                    checked={configDraft.includes(name)}
                    onChange={() => toggleDraft(name)}
                    className="w-4 h-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-200">{name}</span>
                  <span className="ml-auto text-xs text-gray-400">{formatEUR(valueByStatus[name]?.total || 0)}</span>
                </label>
              ))}
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowConfig(false)} className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                Cancel
              </button>
              <button
                onClick={saveConfig}
                disabled={savingConfig}
                className="flex items-center gap-2 px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                data-testid="finance-config-save-btn"
              >
                {savingConfig ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Project detail modal */}
      {selectedProject && (
        <ProjectModal
          project={selectedProject}
          onClose={() => setSelectedProject(null)}
          onSave={() => {
            setSelectedProject(null);
            loadData();
          }}
        />
      )}
    </div>
  );
};

export default FinanceAdmin;
